<template>
  <div class="h-full overflow-auto bg-neutral-100">
    <div class="">
      <!-- content -->
      <div class="mx-auto mb-5 mt-5 max-w-5xl px-2">
        <div
          class="mx-0 mb-4 mt-4 bg-sky-50 shadow sm:mx-5 sm:my-5 sm:rounded-lg md:mx-8 md:my-8 xl:mx-auto xl:max-w-5xl"
        >
          <!-- Card Content goes here -->
          <div class="justify-between px-4 py-5 sm:p-10 lg:flex">
            <div>
              <h1 class="text-2xl font-extrabold">
                {{ appSettingsStore.applicationTitle }}
                <!-- <span class="font-semibold">({{ appSettingsStore.pageTitleBase }})</span> -->
              </h1>
              <h2 class="mt-2 text-xl text-neutral-500 md:mt-1">
                <span class="font-semibold">Welcome, {{ userRef.firstName }}</span>
              </h2>
              <div class="prose prose-neutral mt-4 prose-li:marker:text-neutral-400 md:mt-5">
                <p>Insight is a grant, contract, and project tracking app.</p>
              </div>
            </div>
            <div class="mt-6 items-start justify-start gap-5 space-y-5 sm:flex sm:space-y-0 lg:mt-0">
              <LogoutButton :as="BaseButton" variant="priority-hollow" class="w-full sm:w-auto" label="Sign Out" />
            </div>
          </div>

          <!-- <div class="flex justify-center" v-if="false">
            <h2 class="mt-2 mb-4 text-xl text-sky-500 md:mt-1 md:mb-6">
              View all
              <span class="font-semibold">
                <router-link :to="{ name: handlerNavItem.name }" v-if="handlerNavItem.isAuthorized">
                  <span class="cursor-pointer font-semibold hover:underline">{{ handlerNavItem.label }}</span>
                </router-link></span
              >
            </h2>
          </div> -->
        </div>
        <div class="mx-auto mb-5 max-w-5xl px-2" v-if="navItems.items?.length > 0">
          <div class="mx-3 sm:mx-5 md:mx-8 xl:mx-0">
            <!-- <h2 class="text-sm font-medium text-neutral-500"></h2> -->
            <ul role="list" class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-6">
              <template v-for="item in navItems.items" :key="item.name">
                <SkeletonButton v-if="navItems.isAuthPending" class="col-span-1" />
                <li class="col-span-1 flex rounded-md shadow-sm" v-if="item.isAuthorized">
                  <router-link
                    :to="{ name: item.name }"
                    class="flex flex-1 items-start justify-between rounded-md border border-neutral-200 bg-white text-neutral-900 transition-all hover:border-sky-200 hover:bg-sky-100 hover:text-sky-900 focus:border-sky-200 focus:bg-sky-100 focus:text-sky-900"
                  >
                    <div class="flex-1 px-4 py-2 text-sm">
                      <div class="font-medium">{{ item.label }}</div>
                      <p class="text-neutral-500">
                        {{ item.description }}
                      </p>
                    </div>
                  </router-link>
                </li>
              </template>
            </ul>
          </div>
        </div>
        <!-- <DashboardDataButtonGroup title="Example thing" class="pb-7"
          ><DashboardDataButton
            v-for="item in exampleData"
            :key="item.id"
            :link-to="{ name: routeNames.grant.list, query: { statusMurn: [item.statusMurn] } }"
            :description="item.longDescription"
            :data-value="item.count"
          >
            <template #additional>
              <Badge :variant="item.badgeColor" :label="item.amount" bordered></Badge>
            </template>
          </DashboardDataButton>
        </DashboardDataButtonGroup> -->
        <DashboardDataButtonGroup
          title="Environment Information Management Projects by Status"
          class="pb-7"
          v-if="projects && projects.length > 0"
          ><DashboardDataButton
            v-for="item in projects"
            :key="item.statusMurn"
            :link-to="{ name: routeNames.project.list, query: { statusMurnIn: [item.statusMurn] } }"
            :description="item.longDescription"
            :data-value="item.count"
          >
            <template #additional>
              <Badge
                :murn="item.statusMurn"
                :label="`$${formatNumber(item.amount, { decimalPlaces: 0 })}`"
                bordered
              ></Badge>
            </template>
          </DashboardDataButton>
        </DashboardDataButtonGroup>
      </div>
    </div>
    <footer class="sticky top-[100vh] mt-20 flex h-10 flex-col items-center p-2">
      <MdrnAttribution />
    </footer>
  </div>
</template>

<script lang="ts">
import { InitialBadge, Badge, BadgeColor } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnAttribution, SkeletonButton, useAppNav, useAppSettingsStore } from "@modernary/mui/components/common";
import { useUser } from "@modernary/mui/utils/authentication";
import { LoginButton, LogoutButton, RegisterButton } from "@modernary/mui/utils/authentication/buttons";
import { useAuthorization } from "@modernary/mui/utils/authorization";
import { routeNames } from "@/routes";
import { computed, defineComponent, ref } from "vue";
import { formatNumber } from "@modernary/mui/utils/filters";
import DashboardDataButtonGroup from "@/components/DashboardDataButtonGroup.vue";
import DashboardDataButton from "@/components/DashboardDataButton.vue";
import { useApi } from "@modernary/mui/utils/api";
import { getProjects } from "@/services/endpoints";
import projectService from "@/views/project/projectService";
import { adaptPagedResponse } from "@modernary/mui/components/lookup";

export default defineComponent({
  name: "HomeInternal",
  setup() {
    const { userRef } = useUser();
    const { authorizationClient, AuthRequest, handlePreempts } = useAuthorization();
    const appSettingsStore = useAppSettingsStore();

    const { navigationItems, userNavigationItems } = useAppNav();

    const navItems = computed(() => {
      const entities = navigationItems?.find((x) => x.label != "home");
      const items = entities?.items;
      if (!items || !entities.isAuthorized) {
        return { items: [], isAuthorized: ref(false), isAuthPending: ref(false) };
      }
      return {
        items: items,
        isAuthorized: entities.isAuthorized,
        isAuthPending: entities.isAuthPending,
      };
    });

    const projects = ref();

    // const exampleData = [
    //   {
    //     id: "1",
    //     longDescription: "Submitted Grants",
    //     statusMurn: "murn:insight:lookup/GrantStatus/Submitted",
    //     amount: `$${formatNumber(1000000, { decimalPlaces: 0 })}`,
    //     count: 5,
    //     badgeColor: BadgeColor.NEUTRAL,
    //   },
    //   {
    //     id: "2",
    //     longDescription: "Awarded Grants",
    //     statusMurn: "murn:insight:lookup/GrantStatus/Awarded",
    //     amount: `$${formatNumber(4000000, { decimalPlaces: 0 })}`,
    //     count: 10,
    //     badgeColor: BadgeColor.GREEN,
    //   },
    //   //todo - fix hard coded vals
    //   {
    //     id: "3",
    //     longDescription: "Denied Grants",
    //     statusMurn: "murn:insight:lookup/GrantStatus/Denied",
    //     amount: `$${formatNumber(50000, { decimalPlaces: 0 })}`,
    //     count: 2,
    //     badgeColor: BadgeColor.RED,
    //   },
    // ];

    const { exec: fetchProjects } = useApi(getProjects, {
      responseAdapter: async (response) => {
        return await adaptPagedResponse(response, projectService.responseAdapter);
      },
    });

    const projectStatusMap = new Map();
    const getProjectsByStatus = () => {
      fetchProjects().then((result) => {
        if (!result) return;
        result._items.forEach((item) => {
          let status = projectStatusMap.get(item.statusMurn);
          if (!status) {
            status = {
              id: "1",
              longDescription: item.status?.description,
              statusMurn: item.statusMurn,
              amount: 0,
              count: 0,
            };
            projectStatusMap.set(item.statusMurn, status);
          }
          status.amount += item.budgetAmount;
          status.count += 1;
        });

        //Format amounts here before sending up there or keep formatting up there?
        projects.value = Array.from(projectStatusMap.values());
      });
    };

    getProjectsByStatus();

    return {
      // exampleData,
      userRef,
      routeNames,
      appSettingsStore,
      BaseButton,
      formatNumber,
      BadgeColor,
      projects,
      navItems,
    };
  },
  components: {
    Badge,
    DashboardDataButtonGroup,
    DashboardDataButton,
    BaseButton,
    InitialBadge,
    LoginButton,
    RegisterButton,
    MdrnAttribution,
    LogoutButton,
    SkeletonButton,
  },
});
</script>
